/**
 * jrs-v2-configuration-parser.js — cart-line → state.draft inverse
 *
 * Phase 4 cart-edit, session 1 commit 1.
 *
 * The inverse of the cart-submit chain. Walks a set of related cart line
 * items (one configuration's worth: parent product line(s) + the
 * per-position branding child lines) and emits a state.draft fragment
 * shaped to drop into the popup's existing state shape so cart-edit
 * re-entry pre-fills every step.
 *
 * Forward:  state.draft → ProductConfiguration  → cart line items
 *           (adapter)                            (cart-payload-builder)
 *
 * Reverse:  cart line items → state.draft fragment
 *           (this module)
 *
 * Per Doc 9 §3 and Phase 4 plan §3 commit 1.
 *
 * Bundle parsing landed in Phase 4 v2 (after Phase 3 confirmed Doc 2 §6
 * is correct and V3 bundle cart-submit produces the spec'd 1-parent +
 * N-children shape — see docs/phase-3-bundle-variant-id-diagnosis.md).
 * Calling parse() with flow: 'bundle' (or letting auto-detect identify
 * a bundle) routes to _parseBundle.
 *
 * Doc 9 §3.2 field-name reconciliation (per Phase 4 plan §2 Q2 sub-note):
 *   - Doc 9 says `logo_strategy: 'same' | 'different'`. We emit Phase 2's
 *     `'same_logo' | 'different_logos'`. Doc 9 change-log row pending in
 *     Phase 5 polish.
 *   - Doc 9 says `logo_assignments[pid] = { type, logo_id, text_lines, ... }`.
 *     We emit Phase 2's `{ source, logoId, url, filename, previewText }`.
 *   - Doc 9 says `flow: 'cart-edit'` lives in the draft. Phase 2 puts the
 *     flow on state, not draft — the reducer sets `state.flow='cart-edit'`
 *     directly on OPEN_FOR_CART_EDIT. The parser doesn't emit it.
 *
 * Public API:
 *   JRS.ConfigurationParser.parse({ lineItems, flow })
 *     → { positions, application_methods, logo_strategy, logo_assignments,
 *         text_logo?, sizes? }
 *   JRS.ConfigurationParser.validate(parsed)
 *     → { valid, errors, warnings }
 */

window.JRS = window.JRS || {};

window.JRS.ConfigurationParser = (function () {
  'use strict';

  // ERP parity (2026-08-04) — the POSITION variant for numbered slot n, read
  // from the parent's _customization_data JSON (positions[].variantId).
  function _positionVariantFromCustomizationData(props, n) {
    try {
      var raw = props && props._customization_data;
      if (!raw) return null;
      var parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;
      var list = parsed && parsed.positions;
      if (!list || !list[n - 1]) return null;
      var v = list[n - 1].variantId;
      return (v == null) ? null : String(v);
    } catch (e) { return null; }
  }


  // B4 (2026-08-02) — dual-read for customer-facing keys renamed to hidden
  // underscore form ('_Key' new, 'Key' pre-rename carts). See cart-validator.
  // Numbered-slot read across the 2026-08-20 Tracker rename: new form first
  // ("Logo Id1"), then every pre-rename spelling ("_Logo Id 1" / "Logo Id 1").
  function _slotProp(props, base, n) {
    var v = _dualProp(props, base + ' ' + n);   // canonical (v1.1): space form
    return (typeof v !== 'undefined') ? v : _dualProp(props, base + n);
  }

  function _dualProp(props, key) {
    if (!props) return undefined;
    var u = props['_' + key];
    return (typeof u !== 'undefined') ? u : props[key];
  }


  var MODULE_VERSION = '1.0.0';

  // -------------------------------------------------------------------
  // Public API
  // -------------------------------------------------------------------

  /**
   * Parse a related set of cart line items into a state.draft fragment.
   *
   * @param {object} input
   * @param {Array<object>} input.lineItems - All line items belonging to
   *        ONE configuration (one or more main items sharing the same
   *        _customization_data JSON, plus every branding child whose
   *        _ParentVariant matches one of the mains' variant ids). The
   *        caller (typically the shell's openInEditMode helper) is
   *        responsible for grouping; the parser trusts the input set.
   * @param {string} input.flow - 'multistep' (only flow supported in v1)
   * @returns {object|null} state.draft fragment, or null when the input
   *        is degenerate (empty array). Bundle flow throws.
   */
  function parse(input) {
    if (!input || typeof input !== 'object') {
      throw new Error('JRS.ConfigurationParser.parse: input is required');
    }

    var flow = input.flow;
    if (flow !== 'multistep' && flow !== 'bundle' && flow !== undefined && flow !== null) {
      throw new Error(
        'JRS.ConfigurationParser: unknown flow "' + flow + '". ' +
        'Supports "multistep" or "bundle".'
      );
    }

    var lineItems = input.lineItems || [];
    if (lineItems.length === 0) {
      return null;
    }

    // Auto-detect when flow is unspecified. A line-item set is a bundle
    // iff at least one non-child item carries _bundle_raw (Doc 2 §6.1's
    // canonical "this is a bundle parent" marker). Otherwise multistep.
    if (flow === undefined || flow === null) {
      flow = _detectFlow(lineItems);
    }

    if (flow === 'bundle') {
      return _parseBundle(lineItems);
    }
    return _parseMultistep(lineItems);
  }

  /**
   * Detect bundle vs multistep from line items alone, used when the
   * caller omits `flow`. Looks for _bundle_raw on a non-child item;
   * presence = bundle, absence = multistep.
   *
   * @param {Array<object>} lineItems
   * @returns {string} 'bundle' or 'multistep'
   */
  function _detectFlow(lineItems) {
    for (var i = 0; i < lineItems.length; i++) {
      var props = (lineItems[i] && lineItems[i].properties) || {};
      if (!props._ParentVariant && props._bundle_raw) return 'bundle';
    }
    return 'multistep';
  }

  /**
   * Validate a parsed state.draft fragment. Surface inconsistencies the
   * parser couldn't fix on its own — caller decides whether to proceed
   * (warnings only) or abort (errors).
   *
   * @param {object|null} parsed - Result of parse()
   * @returns {{valid: boolean, errors: string[], warnings: string[]}}
   */
  function validate(parsed) {
    var errors = [];
    var warnings = [];

    if (!parsed || typeof parsed !== 'object') {
      errors.push('parsed is null or not an object');
      return { valid: false, errors: errors, warnings: warnings };
    }

    var positions = parsed.positions || [];
    var hasBranding = positions.length > 0;
    var assignments = parsed.logo_assignments || {};
    var methods = parsed.application_methods || {};

    if (hasBranding) {
      // Every position must have a method + an assignment
      for (var i = 0; i < positions.length; i++) {
        var pid = positions[i];
        if (!methods[pid]) {
          errors.push('Position "' + pid + '" has no application_methods entry');
        } else if (methods[pid] !== 'embroidery' && methods[pid] !== 'print') {
          errors.push('Position "' + pid + '" has invalid method "' + methods[pid] + '"');
        }
        var a = assignments[pid];
        if (!a) {
          errors.push('Position "' + pid + '" has no logo_assignments entry');
          continue;
        }
        if (a.source === 'upload') {
          if (!a.url) errors.push('Position "' + pid + '" upload assignment missing url');
        } else if (a.source === 'existing') {
          if (!a.logoId) errors.push('Position "' + pid + '" existing assignment missing logoId');
        } else if (a.source === 'text') {
          if (!a.previewText) {
            warnings.push('Position "' + pid + '" text assignment has empty previewText');
          }
        } else {
          errors.push('Position "' + pid + '" has unknown source "' + a.source + '"');
        }
      }

      if (parsed.logo_strategy !== 'same_logo' && parsed.logo_strategy !== 'different_logos') {
        errors.push('logo_strategy must be "same_logo" or "different_logos" when positions exist');
      }
    }

    // Sizes: each must have valid variantId + positive quantity
    var sizes = parsed.sizes || [];
    for (var s = 0; s < sizes.length; s++) {
      var size = sizes[s];
      if (!size.variantId || size.variantId <= 0) {
        errors.push('Size entry ' + s + ' has invalid variantId');
      }
      if (!size.quantity || size.quantity <= 0) {
        errors.push('Size entry ' + s + ' has invalid quantity');
      }
    }

    return {
      valid: errors.length === 0,
      errors: errors,
      warnings: warnings
    };
  }

  // -------------------------------------------------------------------
  // Multistep parser
  // -------------------------------------------------------------------

  /**
   * Partition line items into main vs branding, parse positions/methods
   * /logos off the FIRST main item's numbered properties (all mains share
   * an identical numbered-property set), parse sizes from the union of
   * main items.
   */
  function _parseMultistep(lineItems) {
    var partition = _partitionItems(lineItems);
    var mains = partition.mains;
    var children = partition.children;

    if (mains.length === 0 && children.length > 0) {
      // Orphan branding (parent removed, children somehow remain).
      // Surface as null + caller validates; callers should treat as
      // an error condition and avoid opening cart-edit.
      return {
        positions: [],
        application_methods: {},
        logo_strategy: null,
        logo_assignments: {},
        text_logo: null,
        sizes: [],
        _parse_warning: 'orphan_branding_no_main'
      };
    }

    // Sizes: one entry per main item, the variant being the main's id.
    // Multi-size flow produces multiple mains with identical numbered
    // properties; single-size produces exactly one.
    var sizes = _extractSizes(mains);

    // No branding children → unbranded product (the Phase 2 intro-skip
    // path stores no Position N props). Return the shape an unbranded
    // cart-edit would need; consumers can render a "no branding yet,
    // add some?" review variant.
    var firstMain = mains[0];
    var mainProps = (firstMain && firstMain.properties) || {};
    var hasNumberedPositions = !!mainProps['Position 1'];

    if (!hasNumberedPositions) {
      return {
        positions: [],
        application_methods: {},
        logo_strategy: null,
        logo_assignments: {},
        text_logo: null,
        sizes: sizes
      };
    }

    var parsedFromNumbered = _parseNumberedPositions(mainProps);
    var positions = parsedFromNumbered.positions;
    var positionNames = parsedFromNumbered.positionNames;
    var methods = parsedFromNumbered.methods;
    var assignments = parsedFromNumbered.assignments;
    var logoStrategy = _detectLogoStrategy(assignments, positions);

    // Reconstruct a shared text_logo if any assignment is text-source.
    // Phase 2's state.draft.text_logo is a single shared text-logo
    // record (lines + font + colour) — same_logo broadcasts copy it,
    // different_logos sets it per the current iteration. For cart-edit
    // re-entry we set it from the first text-source assignment found
    // so text_builder rehydrates correctly.
    var textLogo = _extractSharedTextLogo(assignments);

    return {
      positions: positions,
      application_methods: methods,
      logo_strategy: logoStrategy,
      logo_assignments: assignments,
      text_logo: textLogo,
      sizes: sizes,
      // Diagnostic: position display names (kept for debugging — the
      // popup itself looks names up via JRS.PositionCatalogue.getById).
      _position_names: positionNames
    };
  }

  // -------------------------------------------------------------------
  // Bundle parser
  // -------------------------------------------------------------------

  /**
   * Bundle inverse of cart-payload-builder.buildBundlePayload.
   *
   * Cart-line input shape (Doc 2 §6 + production /cart.js capture
   * 2026-05-21):
   *   - 1 main item: bundle parent variant (e.g. id=45602342666483),
   *     properties include _bundle_raw, _bundle_instance_id, _bundle
   *     URL, _customization_data, plus numbered position properties
   *     (Position N, Application Method N, Logo Id N, Logo Type N,
   *     Logo N, Logo Filename N, _logoVariant N, _logoproduct N,
   *     _logoposition N, Logo Text N/Logo Font N/Logo Color N for
   *     text logos)
   *   - N branding children: id = position variant (e.g. 43625637970100),
   *     properties._ParentVariant matches main's variant id, properties
   *     include Position, Application Method, Logo Id, Logo, Logo
   *     Type, Logo Filename, Logo Text/Font/Color, _BundleItemName,
   *     _BundleCategoryName, _BundleCategoryIndex, _BundleCategoryProductCount,
   *     _BundleCategoryProducts (CSV), _ProductVariantId,
   *     _ProductSku, _ProductSize, _ProductColor, _qty, _lineItemKey,
   *     _bundle_instance_id (mirrors main's).
   *
   * Output: state.draft fragment matching what the V3 bundle popup
   * would have at the review step (positions + application_methods +
   * logo_strategy + logo_assignments + text_logo + sizes), extended
   * with bundle-specific fields:
   *   branding_strategy: 'bundle_wide' | 'per_product' — detected
   *     from the cardinality of (position → categories) in children.
   *     If every position appears in multiple categories the popup
   *     was in bundle-wide mode (one logo applied across all
   *     categories — Doc 2 §2.3 worked example, Step 0A). If each
   *     position is confined to exactly one category and there are
   *     multiple positions, the popup was in per-product mode
   *     (different logo per category — Doc 2 §2.4 worked example,
   *     Step 0B). Single-position single-category and mixed cases
   *     default to bundle_wide.
   *   position_categories: { positionId → categoryIndex } — only
   *     populated when branding_strategy === 'per_product'. The
   *     cart-payload-builder's _buildCustomization reads this map
   *     for the per-product path.
   *   _bundle_meta: { bundleProductVariantId, bundleProductHandle,
   *     bundleInstanceId } — auxiliary metadata. cart-edit-flow's
   *     _buildSyntheticContext can read this without re-deriving
   *     from the clicked cart line.
   *   _bundle_children: [{categoryName, categoryIndex, productCount,
   *     products, sizes}] — bundle composition reconstructed from
   *     children. Mirrors the shape window.JRS_PDP_CONTEXT.bundle_children
   *     carries on a bundle PDP, so the cart-submit adapter's
   *     _buildBundleConfiguration can route a cart-edit save through
   *     the same _buildBundleCategories path PDP cart-add uses.
   *
   * Defensive: _bundle_raw content is NOT read for reconstruction.
   * The parser uses _bundle_raw presence as the dispatch signal but
   * relies entirely on structured numbered properties + child
   * properties for the draft. This makes the parser tolerant of
   * _bundle_raw schema drift / corruption / truncation, and tolerant
   * of placeholder content in tests.
   *
   * @param {Array<object>} lineItems
   * @returns {object} state.draft fragment
   */
  function _parseBundle(lineItems) {
    var partition = _partitionItems(lineItems);
    var mains = partition.mains;
    var children = partition.children;

    // Find the bundle main — the non-child item carrying _bundle_raw.
    // A multistep main item would slip into _parseBundle only if the
    // caller forced flow='bundle' on a multistep cart-line set; surface
    // as warning + return defensive shape.
    var bundleMain = _findBundleMain(mains);
    if (!bundleMain) {
      return {
        positions: [],
        application_methods: {},
        logo_strategy: null,
        logo_assignments: {},
        text_logo: null,
        sizes: [],
        branding_strategy: null,
        position_categories: {},
        _position_names: {},
        _bundle_meta: null,
        _bundle_children: [],
        _parse_warning: 'no_bundle_main'
      };
    }

    if (children.length === 0) {
      return {
        positions: [],
        application_methods: {},
        logo_strategy: null,
        logo_assignments: {},
        text_logo: null,
        sizes: [],
        branding_strategy: null,
        position_categories: {},
        _position_names: {},
        _bundle_meta: _extractBundleMeta(bundleMain),
        _bundle_children: [],
        _parse_warning: 'no_bundle_children'
      };
    }

    var mainProps = bundleMain.properties || {};
    var parsedFromNumbered = _parseNumberedPositions(mainProps);
    var positions = parsedFromNumbered.positions;
    var positionNames = parsedFromNumbered.positionNames;
    var methods = parsedFromNumbered.methods;
    var assignments = parsedFromNumbered.assignments;
    var occurrences = parsedFromNumbered.occurrences;

    var bundleChildren = _reconstructBundleChildren(children);
    _attachCategoryProductMeta(bundleChildren, mainProps);
    var bundleMeta = _extractBundleMeta(bundleMain);

    // Per_product reconstruction (repeating-position safe). When the parent
    // carries `_logoproduct N` markers resolving to ≥2 categories, build
    // category_configs directly — keyed by real categoryIndex, each category
    // holding its OWN positions/methods/logos/variants. This is the canonical
    // shape the review, cart-submit and save read; the flat positions collapse
    // a repeating position name, so per_product must not use them. Output
    // mirrors what CartEditFlow.convertToPerProduct produced (which now passes
    // this through), just correct for repeats too.
    var categoryNameToIndex = _buildCategoryNameToIndex(children);
    var perProduct = _detectPerProduct(occurrences, categoryNameToIndex);
    if (perProduct.isPerProduct) {
      var categoryConfigs = _buildCategoryConfigs(occurrences, perProduct.resolvedCategoryIndexes);
      // Collapsed positionId → categoryIndex map, kept for back-compat /
      // diagnostics only; category_configs is authoritative for per_product.
      var posCats = {};
      for (var pcI = 0; pcI < occurrences.length; pcI++) {
        var pcPid = occurrences[pcI].positionId;
        if (posCats[pcPid] == null) posCats[pcPid] = perProduct.resolvedCategoryIndexes[pcI];
      }
      return {
        positions: [],
        application_methods: {},
        logo_strategy: null,
        logo_assignments: {},
        text_logo: null,
        sizes: [],
        branding_strategy: 'per_product',
        category_configs: categoryConfigs,
        _current_category_index: Object.keys(categoryConfigs).length,
        position_categories: posCats,
        _position_names: positionNames,
        _bundle_meta: bundleMeta,
        _bundle_children: bundleChildren
      };
    }

    // bundle_wide (unchanged): one flat position set broadcast across every
    // category by the cart-payload-builder's useBundleWide branch.
    var strategyDetection = _detectBundleStrategy(positions, positionNames, children);
    var textLogo = _extractSharedTextLogo(assignments);
    var logoStrategy = _detectLogoStrategy(assignments, positions);

    return {
      positions: positions,
      application_methods: methods,
      logo_strategy: logoStrategy,
      logo_assignments: assignments,
      text_logo: textLogo,
      sizes: [],
      branding_strategy: strategyDetection.branding_strategy,
      position_categories: strategyDetection.position_categories,
      _position_names: positionNames,
      _bundle_meta: bundleMeta,
      _bundle_children: bundleChildren
    };
  }

  /**
   * Find the bundle parent main item — the non-child carrying
   * _bundle_raw. Returns null if not found (degenerate input).
   */
  function _findBundleMain(mains) {
    for (var i = 0; i < mains.length; i++) {
      var props = (mains[i] && mains[i].properties) || {};
      if (props._bundle_raw) return mains[i];
    }
    return null;
  }

  /**
   * Detect bundle_wide vs per_product from the children's distribution.
   *
   * Heuristic:
   *   For each position name (from parent numbered properties),
   *   collect the set of _BundleCategoryIndex values in children
   *   whose Position matches.
   *
   *   - If ALL positions span exactly 1 category AND there are >1
   *     positions: branding_strategy = 'per_product', and each
   *     position maps to its single category via position_categories.
   *
   *   - Otherwise: branding_strategy = 'bundle_wide'.
   *     Single-position single-category, single-position multi-
   *     category, and any mixed distribution all fall through to
   *     bundle_wide as a defensive default. The cart-payload-builder's
   *     useBundleWide branch broadcasts the position across all
   *     categories — semantically correct for the multi-category case
   *     and benign for the single case.
   *
   * @param {string[]} positions
   * @param {Object<string,string>} positionNames - positionId → display name
   * @param {Array<object>} children
   * @returns {{branding_strategy: string, position_categories: Object<string,number>}}
   */
  function _detectBundleStrategy(positions, positionNames, children) {
    var categoriesPerPosition = {};
    for (var p = 0; p < positions.length; p++) {
      var pid = positions[p];
      var name = positionNames[pid];
      var catSet = {};
      for (var c = 0; c < children.length; c++) {
        var props = children[c].properties || {};
        if (!props.Position) continue;
        if (props.Position !== name) continue;
        var catIdx = parseInt(props._BundleCategoryIndex, 10);
        if (isNaN(catIdx) || catIdx < 0) continue;
        catSet[catIdx] = true;
      }
      categoriesPerPosition[pid] = Object.keys(catSet).map(function (k) { return parseInt(k, 10); });
    }

    // Per-product detection: every position confined to exactly one
    // category, and at least 2 positions total. Single-position cases
    // always fall through to bundle_wide (semantically a position in
    // one category is the bundle-wide case for that 1-position config).
    var allSingleCategory = positions.length > 1;
    for (var i = 0; i < positions.length; i++) {
      if ((categoriesPerPosition[positions[i]] || []).length !== 1) {
        allSingleCategory = false;
        break;
      }
    }

    if (allSingleCategory) {
      var positionCategoriesMap = {};
      for (var j = 0; j < positions.length; j++) {
        positionCategoriesMap[positions[j]] = categoriesPerPosition[positions[j]][0];
      }
      return {
        branding_strategy: 'per_product',
        position_categories: positionCategoriesMap
      };
    }

    return {
      branding_strategy: 'bundle_wide',
      position_categories: {}
    };
  }

  /**
   * Build a category-name → real categoryIndex lookup from the branding
   * children. The parent's numbered `_logoproduct N` carries the category
   * NAME per position; children carry both the name (`_BundleCategoryName` /
   * `_BundleItemName`) and the real `_BundleCategoryIndex`. Bridging them lets
   * a repeating position name (same `Position N` in two categories) be
   * reattributed to its true category. First-write-wins per name.
   */
  function _buildCategoryNameToIndex(children) {
    var map = {};
    for (var i = 0; i < children.length; i++) {
      var props = children[i].properties || {};
      var idx = parseInt(props._BundleCategoryIndex, 10);
      if (isNaN(idx) || idx < 0) continue;
      var n1 = props._BundleCategoryName;
      var n2 = props._BundleItemName;
      if (n1 && map[n1] == null) map[n1] = idx;
      if (n2 && map[n2] == null) map[n2] = idx;
    }
    return map;
  }

  /**
   * Per_product detection that survives repeating position names. Uses the
   * parent's `_logoproduct N` markers (written by the forward path ONLY when
   * the bundle has >1 category — exactly the per_product case) instead of the
   * collapsed positionId set that `_detectBundleStrategy` iterates. Per_product
   * IFF every numbered position carries a resolvable category AND ≥2 distinct
   * categories are present. Fails closed to bundle_wide on any unresolved
   * marker, so an unclassifiable bundle degrades safely rather than corrupting.
   *
   * @returns {{isPerProduct: boolean, resolvedCategoryIndexes?: number[]}}
   */
  function _detectPerProduct(occurrences, categoryNameToIndex) {
    if (!occurrences || occurrences.length < 2) return { isPerProduct: false };
    var cats = {};
    var resolved = [];
    for (var i = 0; i < occurrences.length; i++) {
      var name = occurrences[i].categoryName;
      if (!name) return { isPerProduct: false }; // no per-product marker → bundle_wide
      var ci = categoryNameToIndex[name];
      if (typeof ci !== 'number' || ci < 0) return { isPerProduct: false }; // unresolved → safe fallback
      resolved.push(ci);
      cats[ci] = true;
    }
    if (Object.keys(cats).length < 2) return { isPerProduct: false }; // single category → bundle_wide
    return { isPerProduct: true, resolvedCategoryIndexes: resolved };
  }

  /**
   * Group the numbered-position occurrences into `category_configs` keyed by
   * REAL categoryIndex — the canonical per_product draft shape the review,
   * cart-submit and save all read (mirrors `snapshotCategoryConfig`). Within
   * one category positionIds are unique (the repeat is only ACROSS categories),
   * so the per-category flat maps are collision-free. `logo_variants` carries
   * the original `_logoVariant N` PAIRED WITH its method ({variantId, method}) so
   * the save reuses the exact position variant when the method is unchanged, and
   * re-derives (correctly) when the customer edits the method — instead of always
   * re-deriving from the (null-in-prod) bundle metaobject / catalogue.
   */
  function _buildCategoryConfigs(occurrences, resolvedCategoryIndexes) {
    var configs = {};
    for (var i = 0; i < occurrences.length; i++) {
      var o = occurrences[i];
      var ci = resolvedCategoryIndexes[i];
      if (!configs[ci]) {
        configs[ci] = {
          positions: [],
          application_methods: {},
          logo_strategy: null,
          logo_assignments: {},
          logo_variants: {},
          text_logo: null,
          completed: true
        };
      }
      var cfg = configs[ci];
      if (cfg.positions.indexOf(o.positionId) === -1) cfg.positions.push(o.positionId);
      cfg.application_methods[o.positionId] = o.method;
      if (o.assignment) cfg.logo_assignments[o.positionId] = o.assignment;
      if (o.variantId) cfg.logo_variants[o.positionId] = { variantId: o.variantId, method: o.method };
    }
    var keys = Object.keys(configs);
    for (var k = 0; k < keys.length; k++) {
      var c = configs[keys[k]];
      c.logo_strategy = _detectLogoStrategy(c.logo_assignments, c.positions);
      c.text_logo = _extractSharedTextLogo(c.logo_assignments);
    }
    return configs;
  }

  /**
   * Reconstruct the bundle composition (_bundle_children) from the
   * children's per-line metadata. Groups by _BundleCategoryIndex;
   * dedupes by (categoryIndex, _ProductVariantId) since the same
   * (sub-product, size) appears multiple times when bundle_wide
   * applies multiple positions across the same sub-products.
   *
   * First-write-wins for same (cat, variantId): the cart-payload-
   * builder writes identical _qty per same-(cat, variantId) sibling
   * children, so first-write is safe; defensive against drift where
   * later writes differ.
   *
   * @param {Array<object>} children
   * @returns {Array<{categoryName, categoryIndex, productCount, products, sizes}>}
   */
  function _reconstructBundleChildren(children) {
    var byCatIdx = {};
    var catOrder = [];

    for (var i = 0; i < children.length; i++) {
      var props = children[i].properties || {};
      var catIdx = parseInt(props._BundleCategoryIndex, 10);
      if (isNaN(catIdx) || catIdx < 0) continue;

      if (!byCatIdx[catIdx]) {
        byCatIdx[catIdx] = {
          // Round 5 §4: _BundleItemName is the PRODUCT title again (control
          // parity) — the CATEGORY name lives in _BundleCategoryName. Prefer it;
          // legacy carts fall back to _BundleItemName (which was the product
          // title there too — same semantics as pre-round-2).
          categoryName: props._BundleCategoryName || props._BundleItemName || '',
          categoryIndex: catIdx,
          productCount: parseInt(props._BundleCategoryProductCount, 10) || 0,
          products: _splitCsv(props._BundleCategoryProducts),
          _sizesByVariantId: {}
        };
        catOrder.push(catIdx);
      }

      var entry = byCatIdx[catIdx];
      var productVariantId = parseInt(props._ProductVariantId, 10);
      if (!productVariantId || productVariantId <= 0) continue;

      // First-write-wins. Same (cat, productVariantId) across multiple
      // positions emits sibling children with identical _qty in the
      // cart-payload-builder, so this dedupes correctly.
      if (entry._sizesByVariantId[productVariantId]) continue;

      var qty = parseInt(props._qty, 10);
      if (!isFinite(qty) || qty <= 0) {
        qty = parseInt(children[i].quantity, 10) || 0;
      }
      entry._sizesByVariantId[productVariantId] = {
        variantId: productVariantId,
        sizeLabel: props._ProductSize || '',
        quantity: qty,
        sku: props._ProductSku || '',
        colour: props._ProductColor || '',
        productHandle: (entry.products && entry.products[0]) || ''
      };
    }

    catOrder.sort(function (a, b) { return a - b; });

    var out = [];
    for (var ci = 0; ci < catOrder.length; ci++) {
      var entry2 = byCatIdx[catOrder[ci]];
      var sizeKeys = Object.keys(entry2._sizesByVariantId)
        .map(function (k) { return parseInt(k, 10); })
        .sort(function (a, b) { return a - b; });
      var sizes = [];
      for (var sk = 0; sk < sizeKeys.length; sk++) {
        sizes.push(entry2._sizesByVariantId[sizeKeys[sk]]);
      }
      out.push({
        categoryName: entry2.categoryName,
        categoryIndex: entry2.categoryIndex,
        productCount: entry2.productCount,
        products: entry2.products,
        sizes: sizes
      });
    }
    return out;
  }

  /**
   * Fill each reconstructed category's `imageUrl` + `productName` from the
   * parent's `_bundle_raw` — the ONLY place the per-product garment photo AND the
   * real product name survive on the cart line (the branding children carry
   * position icons + a generic `_BundleItemName` like "Product 1", not the garment
   * photo/name). Feeds the per-product review + position-picker headings on cart-
   * edit reopen, and — carried through the save via `_translateBundleChildren-
   * ForContext` → `buildBundleRawFromConfig` — keeps names+images in the rebuilt
   * `_bundle_raw` so the cart manifest doesn't blank out after an edit. No-op when
   * `_bundle_raw` is absent/unparseable or a category has no product data.
   * Shape: `[{ categoryIndex, products: { <handle>: { name, image, ... } } }]` (Doc 2 §9).
   */
  function _attachCategoryProductMeta(bundleChildren, mainProps) {
    if (!bundleChildren || !bundleChildren.length || !mainProps || !(mainProps._bundle_meta || mainProps._bundle_raw)) return;
    var _src = mainProps._bundle_meta || mainProps._bundle_raw; // meta-first (2026-08-20)
    var parsed;
    if (_src && typeof _src === 'object') { parsed = _src; }
    else { try { parsed = JSON.parse(_src); } catch (e) { return; } }
    if (!Array.isArray(parsed)) return;

    var byIndex = {};
    for (var i = 0; i < parsed.length; i++) {
      var cat = parsed[i] || {};
      var ci = parseInt(cat.categoryIndex, 10);
      if (isNaN(ci) || ci < 0) continue;
      var products = cat.products || {};
      var handles = Object.keys(products);
      var img = '';
      var pname = '';
      for (var h = 0; h < handles.length; h++) {
        var prod = products[handles[h]] || {};
        if (!img && prod.image) img = _normaliseImageUrl(prod.image);
        if (!pname && prod.name) pname = String(prod.name);
        if (img && pname) break;
      }
      byIndex[ci] = { imageUrl: img, productName: pname };
    }

    for (var c = 0; c < bundleChildren.length; c++) {
      var entry = bundleChildren[c];
      var meta = entry && byIndex[entry.categoryIndex];
      if (!meta) continue;
      if (!entry.imageUrl && meta.imageUrl) entry.imageUrl = meta.imageUrl;
      if (!entry.productName && meta.productName) entry.productName = meta.productName;
    }
  }

  // Protocol-relative bundle image URLs (`//cdn…`) → https so the review <img>
  // renders reliably off a cart page. Absolute / relative URLs pass through.
  function _normaliseImageUrl(url) {
    var s = String(url || '');
    if (s.indexOf('//') === 0) return 'https:' + s;
    return s;
  }

  function _splitCsv(s) {
    if (!s || typeof s !== 'string') return [];
    var parts = s.split(',');
    var out = [];
    for (var i = 0; i < parts.length; i++) {
      var trimmed = parts[i].replace(/^\s+|\s+$/g, '');
      if (trimmed.length > 0) out.push(trimmed);
    }
    return out;
  }

  /**
   * Extract bundle metadata from the main item.
   *   bundleProductVariantId: from main.id / main.variant_id
   *   bundleProductHandle:    from _bundle URL ('.../products/<handle>')
   *                           with fallback to main.handle
   *   bundleInstanceId:       from properties._bundle_instance_id
   *
   * @param {object} bundleMain
   * @returns {{bundleProductVariantId: number, bundleProductHandle: string, bundleInstanceId: string}}
   */
  function _extractBundleMeta(bundleMain) {
    var props = (bundleMain && bundleMain.properties) || {};
    var variantId = parseInt((bundleMain && (bundleMain.id || bundleMain.variant_id)) || 0, 10) || 0;
    var handle = '';
    var bundleUrl = props._bundle || '';
    var handleMatch = String(bundleUrl).match(/\/products\/([^/?#]+)/);
    if (handleMatch && handleMatch[1]) {
      handle = handleMatch[1];
    }
    if (!handle && bundleMain && bundleMain.handle) {
      handle = bundleMain.handle;
    }
    return {
      bundleProductVariantId: variantId,
      bundleProductHandle: handle,
      bundleInstanceId: props._bundle_instance_id || ''
    };
  }

  /**
   * Partition: main items have no `_ParentVariant` property; branding
   * children do. Some carts may contain unrelated items mixed in — the
   * caller is expected to pre-filter; we still defensively look for the
   * marker properties.
   */
  function _partitionItems(lineItems) {
    var mains = [];
    var children = [];
    for (var i = 0; i < lineItems.length; i++) {
      var item = lineItems[i] || {};
      var props = item.properties || {};
      if (props._ParentVariant) {
        children.push(item);
      } else {
        mains.push(item);
      }
    }
    return { mains: mains, children: children };
  }

  /**
   * Sizes array: one entry per main item with { variantId, sizeLabel,
   * quantity, sku }. The sizeLabel comes from the branding children's
   * _ProductSize property when the main item doesn't expose it directly
   * (multistep main lines don't carry _ProductSize in the cart-payload-
   * builder output; only bundle children do). For multistep we use the
   * variant_id alone and let the popup's downstream consumers (review,
   * cart-submit re-build) re-resolve the label via the PDP trigger's
   * variantSizeMap when the popup is mounted on a PDP. For cart-edit
   * the popup is NOT on a PDP, so sizeLabel will stay empty unless
   * we look it up — but Phase 4 v1 doesn't need the label for any
   * step controller (the review step shows the product variant title
   * via Shopify's variant data, not via the size label).
   */
  function _extractSizes(mains) {
    var sizes = [];
    for (var i = 0; i < mains.length; i++) {
      var item = mains[i] || {};
      var variantId = parseInt(item.id || item.variant_id, 10);
      var quantity = parseInt(item.quantity, 10);
      if (!variantId || variantId <= 0) continue;
      if (!isFinite(quantity) || quantity <= 0) continue;
      sizes.push({
        variantId: variantId,
        sizeLabel: '', // see comment above
        quantity: quantity,
        sku: (item.properties && item.properties._ProductSku) || ''
      });
    }
    return sizes;
  }

  /**
   * Walk Position 1, Position 2, ... on the parent's numbered properties.
   * Builds positions[], application_methods{}, logo_assignments{} keyed
   * by the catalogue-resolved position id (handle form). Stops at the
   * first gap (no Position N for some N) — defensive against drift.
   *
   * Catalogue lookup: JRS.PositionCatalogue.getAll() returns descriptors
   * with { id, name, ... }. We match by name → id; if the name doesn't
   * resolve to a catalogue entry we fall back to a normalised id derived
   * from the display name (lowercase + non-alphanumeric → underscore).
   * The fallback keeps the parser running on positions that are no longer
   * in the catalogue (e.g. a legacy position the merchant removed); a
   * downstream validator warning surfaces.
   */
  function _parseNumberedPositions(mainProps) {
    var positions = [];
    var positionNames = {};
    var methods = {};
    var assignments = {};
    // occurrences: one entry PER numbered position, in order, WITHOUT dedup —
    // so a position name that repeats across categories (e.g. "Large Back" on
    // both a polo and a hoodie) is preserved as two entries. Each carries the
    // owning category (`_logoproduct N`) and the original position variant
    // (`_logoVariant N`) so per_product reconstruction + faithful re-save work.
    // The flat positions/methods/assignments above still collapse (last-write-
    // wins) — fine for bundle_wide, which has no repeats.
    var occurrences = [];

    // Build name → id lookup from the catalogue. If the catalogue isn't
    // available we still proceed using fallback normalisation.
    var catalogueByName = _buildCatalogueNameLookup();

    for (var n = 1; n <= 20; n++) { // safety upper bound
      var positionName = _slotProp(mainProps, 'Position', n);
      if (!positionName) break;

      var positionId = _resolvePositionId(positionName, catalogueByName);
      positions.push(positionId);
      positionNames[positionId] = positionName;

      var methodDisplay = _slotProp(mainProps, 'Application Method', n) || 'Embroidery';
      var method = _normaliseMethod(methodDisplay);
      methods[positionId] = method;

      var assignment = _parseLogoFromNumberedProps(mainProps, n);
      if (assignment) {
        assignments[positionId] = assignment;
      }

      // ERP parity (2026-08-04): _logoVariant N now carries the LOGO PRODUCT
      // variant (control semantics). The POSITION variant lives in
      // _customization_data.positions[].variantId — prefer it; legacy carts
      // (where _logoVariant N WAS the position variant) fall back unchanged.
      var rawVariant = _positionVariantFromCustomizationData(mainProps, n);
      if (rawVariant == null || String(rawVariant) === '') rawVariant = mainProps['_logoVariant' + n];
      var variantId = (rawVariant != null && String(rawVariant) !== '')
        ? parseInt(rawVariant, 10) : null;
      occurrences.push({
        positionId: positionId,
        positionName: positionName,
        method: method,
        assignment: assignment || null,
        categoryName: mainProps['_logoproduct' + n] || null,
        variantId: (variantId && variantId > 0) ? variantId : null
      });
    }

    return {
      positions: positions,
      positionNames: positionNames,
      methods: methods,
      assignments: assignments,
      occurrences: occurrences
    };
  }

  /**
   * Parse the per-position logo block off the parent's numbered
   * properties. Phase 2 emits THREE shapes per Doc 2 §10 + the
   * cart-payload-builder:
   *
   *   upload:    { 'Logo Type N': 'Upload Your Logo',
   *                'Logo Id N':   '<id>',
   *                'Logo N':      '<url>',
   *                'Logo Filename N': '<name>' }
   *
   *   existing:  { 'Logo Type N': 'Existing Logo',
   *                'Logo Id N':   '<id>',
   *                'Logo N':      '<url>',
   *                'Logo Filename N': '<name>' }
   *              (same as upload in cart-line shape — the source distinction
   *              lives only in state.draft and the popup UI. For cart-edit
   *              we infer source='upload' when we see a URL; the customer
   *              re-clicking through the saved-logo gallery would re-flip
   *              to source='existing'. Distinguishing on parse is OK to
   *              skip — the saveLogo integration treats both identically.)
   *
   *   text:      { 'Logo Type N': 'Text Logo',
   *                'Logo Id N':   '<id>',
   *                'Logo Text N': '<line 1>',
   *                'Logo Text 2 N': '<line 2>', ...
   *                'Logo Color N': '<colour>',
   *                'Logo Font N':  '<font>' }
   */
  function _parseLogoFromNumberedProps(props, n) {
    var logoId = _slotProp(props, 'Logo Id', n) || '';
    // §3 (2026-08-04): customer note round-trips through cart-edit. New
    // payloads carry visible 'Note N'; pre-rename carts carry '_Note N'.
    var note = _slotProp(props, 'Note', n) || '';
    var logoType = _slotProp(props, 'Logo Type', n) || '';
    var url = _slotProp(props, 'Logo', n) || '';
    var filename = props['Logo Filename ' + n] || '';
    var firstTextLine = props['Logo Text ' + n] || '';

    var isText = (logoType === 'Text Logo' || logoType === 'Text logo' || logoType === 'text');
    var isExisting = (logoType === 'Existing Logo' || logoType === 'existing');

    if (isText || (!url && firstTextLine)) {
      // Text logo. Reconstruct the lines array from numbered props.
      var lines = [];
      if (firstTextLine) lines.push(firstTextLine);
      var l2 = _dualProp(props, 'Logo Text 2 ' + n);
      var l3 = _dualProp(props, 'Logo Text 3 ' + n);
      var l4 = _dualProp(props, 'Logo Text 4 ' + n);
      if (l2) lines.push(l2);
      if (l3) lines.push(l3);
      if (l4) lines.push(l4);
      return {
        source: 'text',
        logoId: logoId,
        previewText: lines[0] || '',
        text_lines: lines,
        text_font: _dualProp(props, 'Logo Font ' + n) || 'Standard',
        text_color: _dualProp(props, 'Logo Color ' + n) || '#000000',
        note: note
      };
    }

    if (url) {
      return {
        // Source distinction (upload vs existing) doesn't round-trip from
        // cart properties — both shapes emit the same URL/filename pair.
        // Default to 'existing' because cart-edit re-entry implies the
        // logo has been saved (it survived the cart), so it lives in
        // saved logos. The customer can re-upload to flip back to 'upload'.
        source: isExisting ? 'existing' : 'existing',
        logoId: logoId,
        url: url,
        filename: filename,
        note: note
      };
    }

    return null;
  }

  /**
   * Detect whether the customer used same_logo (one logo across every
   * position) or different_logos (per-position selection).
   *
   * Returns null when there are zero positions. Returns 'same_logo' for
   * a single-position assignment (Phase 2 default — the strategy step
   * is skipped on single position so the value isn't user-chosen, but
   * 'same_logo' is the sensible default).
   */
  function _detectLogoStrategy(assignments, positions) {
    if (positions.length === 0) return null;
    if (positions.length === 1) return 'same_logo';
    var firstId = null;
    var allSame = true;
    for (var i = 0; i < positions.length; i++) {
      var a = assignments[positions[i]];
      if (!a) { allSame = false; break; }
      if (firstId === null) {
        firstId = a.logoId;
      } else if (a.logoId !== firstId) {
        allSame = false;
        break;
      }
    }
    return allSame ? 'same_logo' : 'different_logos';
  }

  /**
   * If any position uses a text logo, reconstruct state.draft.text_logo
   * from its assignment so the logo_text_builder controller's
   * onEnter-time rehydration (see Phase 2 Bug 6 fix) finds the data it
   * expects.
   *
   * Multiple positions with DIFFERENT text logos (rare): we use the first
   * encountered. The text_builder rehydrates that one; the customer
   * navigates back through the per-position iteration if they want to
   * edit the others. A more elaborate per-position text_logo store would
   * require state-shape changes outside this commit.
   */
  function _extractSharedTextLogo(assignments) {
    var keys = Object.keys(assignments);
    for (var i = 0; i < keys.length; i++) {
      var a = assignments[keys[i]];
      if (a && a.source === 'text') {
        return {
          lines: (a.text_lines && a.text_lines.length) ? a.text_lines.slice() : [a.previewText || ''],
          font: a.text_font || 'Standard',
          color: a.text_color || '#000000'
        };
      }
    }
    return null;
  }

  // -------------------------------------------------------------------
  // Catalogue + name normalisation helpers
  // -------------------------------------------------------------------

  function _buildCatalogueNameLookup() {
    var byName = {};
    if (!window.JRS || !JRS.PositionCatalogue || typeof JRS.PositionCatalogue.getAll !== 'function') {
      return byName;
    }
    var entries;
    try { entries = JRS.PositionCatalogue.getAll() || []; }
    catch (_e) { return byName; }
    for (var i = 0; i < entries.length; i++) {
      var d = entries[i];
      if (!d) continue;
      if (d.name) byName[_normaliseName(d.name)] = d.id;
      if (d.id) byName[_normaliseName(d.id)] = d.id;
    }
    return byName;
  }

  function _resolvePositionId(displayName, byName) {
    var key = _normaliseName(displayName);
    if (byName && byName[key]) return byName[key];
    // Fallback: synthesise a handle from the display name. Keeps the
    // parser running for positions that aren't in the catalogue (legacy
    // configurations, merchant-removed positions, etc.). validate()
    // doesn't flag this as an error — the cart-edit flow will continue
    // and the position simply won't match a catalogue descriptor for
    // the chip rendering. Surfaces as the position's display name.
    return key || String(displayName || '').toLowerCase();
  }

  function _normaliseName(s) {
    return String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '');
  }

  function _normaliseMethod(displayMethod) {
    var m = String(displayMethod || '').toLowerCase();
    if (m === 'embroidery') return 'embroidery';
    if (m === 'print') return 'print';
    // Default fallback — Phase 2 controllers expect lowercase 'embroidery'
    // or 'print'. Anything else is a drift; default to embroidery and
    // let validate() surface a warning if it cares.
    return 'embroidery';
  }

  // -------------------------------------------------------------------
  // Public surface
  // -------------------------------------------------------------------

  return {
    parse: parse,
    validate: validate,
    _v: MODULE_VERSION
  };
})();
